import {
  archiveDocumentAssemblerTemplateCategory,
  DOCUMENT_ASSEMBLER_LIMITS,
  IDocumentAssemblerTemplateCategory,
  updateDocumentAssemblerTemplateCategory,
} from '@commenda/api';
import {
  Alert,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import { messageFromApiErrorResponse, pluralize } from '@commenda/utils';
import { useState } from 'react';
import { useNotification } from '../hooks/useNotification';
import { ConfirmationModal } from '../molecules/modals/ConfirmationModal';

interface DocumentAssemblerCategoryEditDialogProps {
  category: IDocumentAssemblerTemplateCategory;
  onClose: () => void;
  onChanged?: () => void;
}

export const DocumentAssemblerCategoryEditDialog = ({
  category,
  onClose,
  onChanged,
}: DocumentAssemblerCategoryEditDialogProps) => {
  const { setNotification } = useNotification();
  const archiveCategoryModalVisibility = useToggle(false);

  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const hasChanged =
    trimmedName !== category.name ||
    trimmedDescription !== (category.description ?? '');
  const canSave = Boolean(trimmedName) && hasChanged && !isSaving;

  const onSubmit = async () => {
    setSubmitError(null);
    setIsSaving(true);

    // Unlike a template's, a category's description may be cleared with ''.
    const response = await updateDocumentAssemblerTemplateCategory(
      category.id,
      { name: trimmedName, description: trimmedDescription }
    );

    if (!response.ok) {
      setSubmitError(
        await messageFromApiErrorResponse(
          response,
          'The category could not be updated.'
        )
      );
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    setNotification({ show: true, valid: true, title: 'Category updated' });
    onChanged?.();
    onClose();
  };

  // Rejecting keeps the confirmation open, which is what puts the server's count in front of the
  // author instead of behind a dismissed toast.
  const onArchiveConfirm = async () => {
    const response = await archiveDocumentAssemblerTemplateCategory(
      category.id
    );

    if (!response.ok) {
      const message = await messageFromApiErrorResponse(
        response,
        'The category could not be archived.'
      );
      setSubmitError(message);
      throw new Error(message);
    }

    setNotification({ show: true, valid: true, title: 'Category archived' });
    onChanged?.();
    onClose();
  };

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-xl">
          <DialogHeader className="flex-col items-start gap-xs">
            <DialogTitle>Edit category</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-xl p-3xl">
            {submitError ? (
              <Alert variant="destructive" description={submitError} />
            ) : null}

            {category.archivedAt ? (
              <Alert
                variant="warning"
                title="This category is archived"
                description="It cannot be edited, and no template can be filed under it."
              />
            ) : null}

            <FieldWrapper>
              <Label htmlFor="document-assembler-edit-category-key">Key</Label>
              <Input
                id="document-assembler-edit-category-key"
                value={category.key}
                disabled
              />
              <p className="txt-extra-small-regular mt-xs text-text-secondary">
                A category’s key never changes — a workflow step scoped to it
                would stop matching.
              </p>
            </FieldWrapper>

            <FieldWrapper>
              <Label htmlFor="document-assembler-edit-category-name" required>
                Name
              </Label>
              <Input
                id="document-assembler-edit-category-name"
                value={name}
                maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxNameLength}
                disabled={Boolean(category.archivedAt)}
                onChange={(event) => setName(event.target.value)}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label
                htmlFor="document-assembler-edit-category-description"
                optional
              >
                Description
              </Label>
              <Textarea
                id="document-assembler-edit-category-description"
                rows={3}
                value={description}
                maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxDescriptionLength}
                disabled={Boolean(category.archivedAt)}
                onChange={(event) => setDescription(event.target.value)}
              />
            </FieldWrapper>

            <p className="txt-extra-small-regular text-text-secondary">
              {pluralize(category.templateCount, 'template')} in use under this
              category. Archived templates are not counted.
            </p>
          </div>

          <DialogFooter className="justify-between">
            {category.archivedAt ? (
              <span />
            ) : (
              <Button
                variant="destructive-secondary"
                size="sm"
                type="button"
                onClick={archiveCategoryModalVisibility.on}
              >
                Archive category
              </Button>
            )}
            <div className="flex items-center gap-md">
              <DialogClose asChild>
                <Button variant="secondary" size="sm" type="button">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                variant="primary"
                size="sm"
                type="button"
                disabled={!canSave || Boolean(category.archivedAt)}
                isLoading={isSaving}
                onClick={onSubmit}
              >
                Save changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={archiveCategoryModalVisibility.value}
        onClose={archiveCategoryModalVisibility.off}
        onConfirm={onArchiveConfirm}
        tone="destructive"
        title="Archive category"
        message="Archiving is not deleting — the category and every template filed under it are kept. The server refuses it while any template that is not archived still names this category, so move or archive those templates first."
        confirmButtonText="Archive"
      />
    </>
  );
};
